import logger from '@clamjs/logger';
import open from 'open';

import { getGitRepoInfo } from '../codebase';
import { getCurrentBranch, getOriginMainBranch } from '../gitlab';
import { getUnknowErrorMsg } from '../tools';
import { getO2AppChanges } from './application';
import { getO2BucApi } from './base';
import type { O2ChangeSchema } from './types';

/** 添加 O2 变更 https://icms.alibaba-inc.com/portal/def-work/docs/t2205024.html */
export async function createO2Change(
  params: {
    repo?: string;
    branchType?: 'exist' | 'new';
    description?: string;
    trunk?: string;
    branch?: string;
    aoneBinds?: string;
    print?: boolean;
  } = {},
) {
  if (!params.branch) {
    const currentBranch = getCurrentBranch();
    const mainBranch = getOriginMainBranch();
    if (currentBranch !== mainBranch) {
      params.branch = getCurrentBranch();
    } else {
      throw new Error('createO2Change 缺少参数 branch，当前分支是主干、无法作为兜底分支');
    }
  }

  const gitRepo = params?.repo || getGitRepoInfo().repo;
  const apiParams = {
    // eslint-disable-next-line camelcase
    branch_type: params?.branchType || 'exist',
    description: params?.description || '[ClamBot] 自动化创建O2变更',
    trunk:
      params?.trunk || (!params.trunk && params?.branchType === 'new' ? getOriginMainBranch() : ''),
    branch: params?.branch,
    // eslint-disable-next-line camelcase
    aone_binds: params?.aoneBinds || '',
  };

  // 路径中参数含有 "/" 的需要编码两次，https://aliyuque.antfin.com/fliggy/engineering/vphc9y
  const apiPath = `/v1.0/work/apps/${encodeURIComponent(
    encodeURIComponent(gitRepo),
  )}/devbranches/add`;
  try {
    const result = await getO2BucApi<O2ChangeSchema>(apiPath, {
      query: {
        appIdorName: encodeURIComponent(gitRepo),
      },
      post: true,
      body: apiParams,
    });
    if (params?.print) {
      logger.note(JSON.stringify(result, null, 2), '添加 O2 变更完成, 返回数据');
    }
    // 打开变更地址，提示用户去绑定工作
    const o2ChangeUrl = `https://space.o2.alibaba-inc.com/app/${result.app_id}/change/${result.id}`;
    logger.success(`关联 O2 变更成功，请前往 O2 平台绑定 Aone 工作项：${logger.link(o2ChangeUrl)}`);
    open(o2ChangeUrl);
    return result;
  } catch (error) {
    const message = getUnknowErrorMsg(error);
    throw new Error(`添加 O2 应用变更失败：${message || '未知错误'}`);
  }
}

/** 读取 O2 变更信息 */
export async function getO2Change(params?: { repo?: string; branch?: string; print?: boolean }) {
  const gitBranch = params?.branch || getCurrentBranch();
  /*
   * 获取变更详情的接口 O2 开放平台上没有，https://aliyuque.antfin.com/fee/fsow8x/iyfghr 列表中有，测试下来 branchId 参数值只支持
   * 变更 id、不支持分支名称，使用分支名、接口报错 ”Cannot read properties of undefined (reading'emp_id')“。附上 O2 研发小蜜咨询单
   * https://links.alibaba-inc.com/app/room/5fbf53f6cd068c04d342e682/65b2114a32aca90777f74b71/
   *
   *     const gitRepo = params?.repo || getGitRepoInfo().repo;
   *     const apiPath = `/v1.0/work/apps/${encodeURIComponent(
   *       encodeURIComponent(gitRepo),
   *     )}/devbranches/21046542`;
   *     const apiPath = `/v1.0/work/apps/${encodeURIComponent(
   *       encodeURIComponent(gitRepo),
   *     )}/devbranches/${encodeURIComponent(encodeURIComponent(gitBranch))}`;
   *     const result = await getO2BucApi<O2ChangeSchema>(apiPath);
   *
   * 换一个实现：appChanges 接口获取所有应用变更 + 通过分支名筛选出当前分支对应的变更详情
   */
  const result = await getO2AppChanges({ repo: params?.repo || '' });
  let curChange;
  if (result) {
    curChange = result.find((change) => change.branch === gitBranch);
    if (curChange?.id) {
      if (params?.print) {
        logger.note(JSON.stringify(curChange, null, 2), '应用变更详情数据');
      }
    }
  }
  return curChange;
}

/** 编辑应用开发分支配置, https://icms.alibaba-inc.com/portal/def-work/docs/t2191442.html */

/** 变更退出迭代集成区, https://icms.alibaba-inc.com/portal/def-work/docs/t2205014.html */

/** 开发分支创建 code review, https://icms.alibaba-inc.com/portal/def-work/docs/t2205015.html */
